import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, Phone, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import FaqBlock, { FaqAnswer, HelpfulWidget, AnchorHeading } from "@/components/FaqBlock";

/**
 * Full FAQ page. Renders every active FAQ item grouped by section, with:
 *   - on-page search filter (question + answer text)
 *   - per-question anchor links (/faq#<slug>) with copy-on-click
 *   - Was-this-helpful widget on every answer
 *   - FAQPage JSON-LD schema (the definitive emission — smaller Question
 *     schemas exist on contextual `FaqBlock` sub-usages but this is the
 *     canonical machine-readable source)
 *   - <link rel="canonical"> + <title> / <meta name="description">
 *
 * We inject the head tags imperatively (no react-helmet dependency required)
 * and clean up on unmount so we don't leak them into other pages.
 */
export default function FaqPage() {
  const [data, setData] = useState({ sections: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const sectionRefs = useRef({});

  // ------------------------------------------------- data fetch
  useEffect(() => {
    api.get("/faq")
      .then((r) => setData(r.data))
      .catch(() => setData({ sections: [] }))
      .finally(() => setLoading(false));
  }, []);

  // ------------------------------------------------- head tags (SEO)
  useEffect(() => {
    if (!data.sections.length) return;
    const tags = [];
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      if (created) tags.push(el);
    };
    const prevTitle = document.title;
    document.title = "Jeweltix FAQ — Jewellery CAD Files, Payments, 3D Printing";
    setMeta("description", "Common questions about buying and selling jewellery CAD files on Jeweltix — 3DM and STL formats, payments, escrow, 3D printing, CAM vendors, and support.");

    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${window.location.origin}/faq`);

    // hreflang — English is the sole active language for now; we still emit
    // the tag so search engines record the current language mapping cleanly.
    let hreflang = document.head.querySelector('link[rel="alternate"][hreflang="en"]');
    const hreflangCreated = !hreflang;
    if (!hreflang) {
      hreflang = document.createElement("link");
      hreflang.setAttribute("rel", "alternate");
      hreflang.setAttribute("hreflang", "en");
      document.head.appendChild(hreflang);
    }
    hreflang.setAttribute("href", `${window.location.origin}/faq`);
    let xdefault = document.head.querySelector('link[rel="alternate"][hreflang="x-default"]');
    const xdefaultCreated = !xdefault;
    if (!xdefault) {
      xdefault = document.createElement("link");
      xdefault.setAttribute("rel", "alternate");
      xdefault.setAttribute("hreflang", "x-default");
      document.head.appendChild(xdefault);
    }
    xdefault.setAttribute("href", `${window.location.origin}/faq`);

    // FAQPage JSON-LD (main emission — all active questions in one block)
    const script = document.createElement("script");
    script.type = "application/ld+json";
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": data.sections.flatMap((s) => s.items.map((it) => ({
        "@type": "Question",
        "name": it.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": it.answer.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1"),
        },
      }))),
    };
    script.text = JSON.stringify(jsonld);
    script.setAttribute("data-jx", "faq-schema");
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      tags.forEach((t) => t.remove());
      if (canonicalCreated) canonical.remove();
      if (hreflangCreated) hreflang.remove();
      if (xdefaultCreated) xdefault.remove();
      script.remove();
    };
  }, [data]);

  // ------------------------------------------------- anchor auto-scroll
  useEffect(() => {
    if (loading || !data.sections.length) return;
    const hash = window.location.hash?.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [loading, data]);

  // ------------------------------------------------- search filter
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.sections;
    return data.sections
      .map((s) => ({
        ...s,
        items: s.items.filter((it) =>
          it.question.toLowerCase().includes(term) ||
          it.answer.toLowerCase().includes(term)
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [q, data]);

  const totalCount = data.sections.reduce((a, s) => a + s.items.length, 0);
  const shownCount = filtered.reduce((a, s) => a + s.items.length, 0);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      {/* --- hero --- */}
      <div className="text-center mb-10 sm:mb-14">
        <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-4">
          <HelpCircle className="h-3.5 w-3.5" /> Help centre
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl">Jeweltix FAQ</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Everything you need to know about buying and selling jewellery CAD files, file formats, payments,
          3D printing partners, and getting help when something goes wrong.
        </p>
      </div>

      {/* --- search --- */}
      <div className="sticky top-2 z-30 bg-background/95 backdrop-blur border border-border rounded-md p-3 mb-8 flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search questions (e.g. STL, payment, KYC, vendor)…"
          data-testid="faq-search"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            data-testid="faq-search-clear"
            className="text-[11px] text-muted-foreground hover:text-foreground font-mono uppercase"
          >
            Clear
          </button>
        )}
        <span className="text-[11px] font-mono text-muted-foreground shrink-0 hidden sm:inline">
          {shownCount}/{totalCount}
        </span>
      </div>

      {/* --- section jump nav (desktop) --- */}
      {!q && data.sections.length > 0 && (
        <nav className="hidden md:flex flex-wrap gap-2 mb-8" data-testid="faq-section-nav">
          {data.sections.map((s) => (
            <a
              key={s.section}
              href={`#section-${slugify(s.section)}`}
              onClick={(e) => {
                e.preventDefault();
                sectionRefs.current[s.section]?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="text-xs font-mono uppercase tracking-wider border border-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors"
              data-testid={`faq-jump-${slugify(s.section)}`}
            >
              {s.section}
            </a>
          ))}
        </nav>
      )}

      {/* --- sections --- */}
      {loading && <p className="text-center text-muted-foreground py-14">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-border rounded-md py-16 text-center text-muted-foreground" data-testid="faq-empty">
          No questions match <b>&quot;{q}&quot;</b> — try a shorter search or browse the sections above.
        </div>
      )}

      <div className="space-y-12">
        {filtered.map((s) => (
          <section
            key={s.section}
            id={`section-${slugify(s.section)}`}
            ref={(el) => (sectionRefs.current[s.section] = el)}
            className="scroll-mt-24"
            data-testid={`faq-section-${slugify(s.section)}`}
          >
            <h2 className="font-display font-bold text-2xl mb-5 text-primary">{s.section}</h2>
            <div className="space-y-6">
              {s.items.map((it) => (
                <article key={it.id} className="border-l-2 border-border pl-5 py-1" data-testid={`faq-article-${it.slug}`}>
                  <AnchorHeading slug={it.slug} question={it.question} level="h3" />
                  <div className="mt-3 space-y-3">
                    <FaqAnswer text={it.answer} />
                    <HelpfulWidget slug={it.slug} />
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* --- contact fallback --- */}
      <div className="mt-16 border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-6 sm:p-8 text-center">
        <h2 className="font-display font-bold text-xl sm:text-2xl">Still have a question?</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-lg mx-auto">
          Our team is happy to help. Reach us on WhatsApp, phone, or the contact form and we'll get back to you.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <a
            href="tel:+919175004668"
            data-testid="faq-contact-phone"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 px-4 py-2 text-sm font-semibold"
          >
            <Phone className="h-4 w-4" /> +91 91750 04668
          </a>
          <a
            href="https://wa.me/919175004668"
            target="_blank" rel="noreferrer"
            data-testid="faq-contact-whatsapp"
            className="inline-flex items-center gap-2 rounded-md border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 px-4 py-2 text-sm font-semibold"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp us
          </a>
          <Link
            to="/grievance"
            data-testid="faq-contact-grievance"
            className="inline-flex items-center gap-2 rounded-md border border-border hover:border-primary px-4 py-2 text-sm font-semibold"
          >
            Raise a formal grievance
          </Link>
        </div>
      </div>
    </div>
  );
}

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
